import axios from 'axios';
import { getStoredToken, clearAuthData, isTokenExpired } from '../utils/auth';

// Gunakan environment variable untuk API base URL
// Default ke same-origin agar request /api diarahkan lewat domain yang sama (Nginx reverse proxy)
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    
    if (token && !isTokenExpired(token)) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor untuk handling error dan token expiration
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response) {
      // Handle token expiration
      if (error.response.status === 401) {
        console.warn('Token expired or invalid, clearing auth data');
        clearAuthData();
        
        // Only redirect if not already on login page and not in the middle of a login request
        const currentPath = window.location.pathname;
        const isLoginRequest = error.config?.url?.includes('/api/token');
        
        if (currentPath !== '/login' && !isLoginRequest) {
          // Use setTimeout to avoid interfering with current request
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }
      }
      
      console.error('API Error:', error.response.data);
      return Promise.reject(error);
    }
    console.error('API Error:', error.message);
    return Promise.reject(error);
  }
);

// Exercise endpoints
export const fetchExcercises = () => api.get('/api/exercise');
export const fetchSingleExcercise = (id) => api.get(`/api/exercise/${id}`);

/**
 * UPDATE EXERCISE REFERENCE METADATA
 * Endpoint: PUT /api/exercise/{exercise_id}/ref-metadata/{section}
 * Description: Updates reference motion metadata for a specific exercise section (begin, middle, end)
 * 
 * Parameters:
 * - exercise_id: integer (exercise ID)
 * - section: string ("begin" | "middle" | "end") - REQUIRED
 * 
 * Request Body:
 * {
 *   "movementRules": {
 *     "lShoulder": "move",
 *     "rShoulder": "fixed",
 *     "lElbow": "ignore"
 *   },
 *   "maxAngleLimits": {
 *     "lShoulder": [45, 90],
 *     "rShoulder": [30, 60]
 *   },
 *   "feedbackPriority": ["lShoulder", "rShoulder", "lElbow"]
 * }
 * 
 * Expected Response:
 * {
 *   "name": "begin",
 *   "movementRules": {...},
 *   "maxAngleLimits": {...},
 *   "feedbackPriority": [...],
 *   "videoPath": null,
 *   "duration": 0,
 *   "path": null,
 *   "format": "TSFrameLog"
 * }
 */
export const updateExcercise = (exerciseId, section, motionData) => {
  // Validate section parameter
  const validSections = ['begin', 'middle', 'end'];
  if (!validSections.includes(section)) {
    throw new Error(`Invalid section. Must be one of: ${validSections.join(', ')}`);
  }

  return api.put(`/api/exercise/${exerciseId}/ref-metadata/${section}`, motionData);
};

export const deleteExercisePhase = (exerciseId, section) => {
  const validSections = ['begin', 'middle', 'end'];
  if (!validSections.includes(section)) {
    throw new Error(`Invalid section. Must be one of: ${validSections.join(', ')}`);
  }
  return api.delete(`/api/exercise/${exerciseId}/ref-metadata/${section}`);
};

// Mirror reference motion data from source exercise to target exercise
export const mirrorExerciseReference = (sourceId, targetId) => {
  return api.post(`/api/exercise/${sourceId}/mirror/${targetId}`);
};

// ============================================================================
// NEW API ENDPOINTS - DOCUMENTATION FOR PYTHON BACKEND TEAM
// ============================================================================

/**
 * 1. CREATE NEW EXERCISE
 * Endpoint: POST /api/exercises
 * Description: Creates a new exercise with basic information
 * 
 * Request Body:
 * {
 *   "title": "Exercise #11",
 *   "description": "Description for exercise #11", 
 *   "repetition": 3,
 *   "holdDuration": 5
 * }
 * 
 * Expected Response:
 * {
 *   "id": 11,
 *   "title": "Exercise #11",
 *   "description": "Description for exercise #11",
 *   "repetition": 3,
 *   "holdDuration": 5,
 *   "thumbnailPath": null,
 *   "previewVideoPath": null,
 *   "referenceMotionData": [],
 *   "motionRecordingData": null
 * }
 */
export const createExercise = (exerciseData) => {
  return api.post(`/api/exercise/${exerciseData.id}`, {
    title: exerciseData.title,
    description: exerciseData.description,
    repetition: exerciseData.repetition,
    referenceMotionData: exerciseData.referenceMotionData
  });
};

/**
 * 2. UPDATE EXERCISE DETAILS
 * Endpoint: PUT /api/exercise/{exerciseId}
 * Description: Updates basic exercise information (title, description, repetition, holdDuration)
 * 
 * Request Body:
 * {
 *   "title": "Updated Exercise Title",
 *   "description": "Updated exercise description",
 *   "repetition": 5,
 *   "holdDuration": 10
 * }
 * 
 * Field Validation:
 * - repetition: integer between 1-100
 * - holdDuration: integer between 1-300 (seconds)
 * 
 * Expected Response:
 * {
 *   "id": 11,
 *   "title": "Updated Exercise Title",
 *   "description": "Updated exercise description",
 *   "repetition": 5,
 *   "holdDuration": 10,
 *   "message": "Exercise updated successfully"
 * }
 */
export const updateExerciseDetails = (exerciseId, data) => {
  return api.patch(`/api/exercise/${exerciseId}`, {
    title: data.title,
    description: data.description,
    repetition: data.repetition,
    qaStatus: data.qaStatus
  });
};

/**
 * 3. UPLOAD EXERCISE THUMBNAIL
 * Endpoint: POST /api/exercises/upload-thumbnail
 * Description: Uploads an image file as exercise thumbnail
 * 
 * Request: multipart/form-data
 * Form Fields:
 * - thumbnail: image file (jpg, png, gif, etc.)
 * - exercise_id: integer (exercise ID)
 * 
 * File Validation:
 * - File type: image/* (jpg, png, gif, webp, etc.)
 * - Max size: 5MB
 * 
 * Expected Response:
 * {
 *   "success": true,
 *   "thumbnailPath": "http://localhost:9000/pivot-pt/exercise/0011/thumbnail.png",
 *   "message": "Thumbnail uploaded successfully"
 * }
 */
export const uploadExerciseThumbnail = (exerciseId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  //formData.append('exercise_id', exerciseId);
  
  return api.put(`/api/exercise/${exerciseId}/thumbnail.png`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

/**
 * 4. UPLOAD EXERCISE VIDEO
 * Endpoint: PUT /api/exercise/{exercise_id}/preview.mp4
 * Description: Uploads a video file as exercise preview video
 * 
 * Request: multipart/form-data
 * Form Fields:
 * - file: video file (mp4, avi, mov, etc.)
 * 
 * File Validation:
 * - File type: video/* (mp4, avi, mov, webm, etc.)
 * - Max size: 50MB
 * 
 * Expected Response:
 * {
 *   "success": true,
 *   "videoPath": "http://localhost:9000/pivot-pt/exercise/0011/preview.mp4",
 *   "message": "Video uploaded successfully"
 * }
 */
export const uploadExerciseVideoPreview = (exerciseId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return api.put(`/api/exercise/${exerciseId}/preview.mp4`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

/**
 * 4.5. UPLOAD EXERCISE REFERENCE VIDEO
 * Endpoint: PUT /api/exercise/{exercise_id}/ref-video/{section}-clip.mp4
 * Description: Uploads a reference video clip for a specific exercise section (begin, middle, end)
 * 
 * Request: multipart/form-data
 * Form Fields:
 * - file: video file (mp4, avi, mov, etc.)
 * 
 * Parameters:
 * - exercise_id: integer (exercise ID)
 * - section: string ("begin" | "middle" | "end") - REQUIRED
 * 
 * File Validation:
 * - File type: video/* (mp4, avi, mov, webm, etc.)
 * - Max size: 50MB
 * 
 * Expected Response:
 * {
 *   "name": "begin",
 *   "videoPath": "http://localhost:9000/pivot-pt/exercise/0011/begin-clip.mp4",
 *   "duration": 5.5,
 *   "path": null,
 *   "format": "TSFrameLog",
 *   "movementRules": {},
 *   "maxAngleLimits": {},
 *   "feedbackPriority": []
 * }
 */
export const uploadExcerciseRefVideo = (exerciseId, section, file) => {
  // Validate section parameter
  const validSections = ['begin', 'middle', 'end'];
  if (!validSections.includes(section)) {
    throw new Error(`Invalid section. Must be one of: ${validSections.join(', ')}`);
  }
  
  const formData = new FormData();
  formData.append('file', file);
  
  return api.put(`/api/exercise/${exerciseId}/ref-video/${section}-clip.mp4`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

/**
 * 4.6. UPLOAD EXERCISE REFERENCE ANIMATION
 * Endpoint: PUT /api/exercise/{exercise_id}/ref-animation/{section}-animation.txt
 * Description: Uploads a reference animation file for a specific exercise section (begin, middle, end)
 * 
 * Request: multipart/form-data
 * Form Fields:
 * - file: animation file (.txt, .json, etc.)
 * 
 * Parameters:
 * - exercise_id: integer (exercise ID)
 * - section: string ("begin" | "middle" | "end") - REQUIRED
 * 
 * File Validation:
 * - File type: animation files (.txt, .json, .fbx, .obj, etc.)
 * - Max size: 20MB
 * 
 * Expected Response:
 * {
 *   "name": "begin",
 *   "videoPath": null,
 *   "duration": 0,
 *   "path": "http://localhost:9000/pivot-pt/exercise/0011/begin-animation.txt",
 *   "format": "TSFrameLog",
 *   "movementRules": {},
 *   "maxAngleLimits": {},
 *   "feedbackPriority": []
 * }
 */
export const uploadExcerciseRefAnim = (exerciseId, section, file) => {
  // Validate section parameter
  const validSections = ['begin', 'middle', 'end'];
  if (!validSections.includes(section)) {
    throw new Error(`Invalid section. Must be one of: ${validSections.join(', ')}`);
  }
  
  const formData = new FormData();
  formData.append('file', file);
  
  return api.put(`/api/exercise/${exerciseId}/ref-animation/${section}-animation.txt`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

/**
 * 5. UPLOAD TSFRAME FILE (Motion Recording & Animation)
 * Endpoint: POST /api/exercises/upload-tsframe-file
 * Description: Uploads a TSFrame file for different purposes (motion recording, animation, etc.)
 * 
 * Request: multipart/form-data
 * Form Fields:
 * - file: TSFrame file (.txt, .json, etc.)
 * - exercise_id: integer (exercise ID)
 * - file_type: string (motion_recording, animation) - REQUIRED
 * - animation_type: string (main, begin, middle, end) - OPTIONAL, only for animation
 * - duration: float (animation duration in seconds) - OPTIONAL, only for animation
 * 
 * File Types:
 * - "motion_recording": Motion recording data file (.txt)
 * - "animation": Animation file (.txt, .json, .fbx, .obj, etc.)
 * 
 * Animation Types (when file_type = "animation"):
 * - "main": Main exercise animation file (default)
 * - "begin": Begin phase animation file
 * - "middle": Middle phase animation file  
 * - "end": End phase animation file
 * 
 * File Validation:
 * - File type: TSFrame files (.txt, .json, .fbx, .obj, .dae, .3ds, .blend)
 * - Max size: 20MB
 * 
 * Expected Response for Motion Recording:
 * {
 *   "success": true,
 *   "motionRecordingData": {
 *     "path": "./exercise-0011-recording.txt",
 *     "format": "TSFrameLog"
 *   },
 *   "message": "Motion recording data uploaded successfully"
 * }
 * 
 * Expected Response for Animation:
 * {
 *   "success": true,
 *   "animationPath": "http://localhost:9000/pivot-pt/exercise/0011/begin-animation.json",
 *   "animationType": "begin",
 *   "duration": 5.5,
 *   "message": "Animation file uploaded successfully"
 * }
 */
export const uploadTSFrameFile = (exerciseId, file, fileType, options = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('exercise_id', exerciseId);
  formData.append('file_type', fileType);
  
  // Add animation-specific options if file type is animation
  if (fileType === 'animation') {
    const { animationType = 'main', duration = null } = options;
    formData.append('animation_type', animationType);
    
    if (duration !== null && duration !== undefined) {
      formData.append('duration', duration);
    }
  }
  
  return api.post('/api/exercises/upload-tsframe-file', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

// Legacy methods for backward compatibility
export const uploadMotionRecordingData = (exerciseId, file) => {
  return uploadTSFrameFile(exerciseId, file, 'motion_recording');
};

export const uploadAnimationFile = (exerciseId, file, animationType = 'main', duration = null) => {
  return uploadTSFrameFile(exerciseId, file, 'animation', { animationType, duration });
};

/**
 * 6. DELETE EXERCISE
 * Endpoint: DELETE /api/exercise/{exercise_id}
 * Description: Deletes an exercise and all its associated data
 * 
 * Expected Response:
 * {
 *   "success": true,
 *   "message": "Exercise deleted successfully"
 * }
 */
export const deleteExercise = (exerciseId) => {
  return api.delete(`/api/exercise/${exerciseId}`);
};

/* export const getProduct = (id) => api.get(`/products/${id}`);
export const createProduct = (productData) => api.post('/products', productData);
export const updateProduct = (id, productData) => api.put(`/products/${id}`, productData);
export const deleteProduct = (id) => api.delete(`/products/${id}`);
 */

// ============================================================================
// USER MANAGEMENT API ENDPOINTS
// ============================================================================

/**
 * CREATE USER
 * Endpoint: POST /api/user
 * Description: Creates a new user for smart suit
 * 
 * Request Body:
 * {
 *   "externalId": 456789,
 *   "name": "janedoe",
 *   "displayName": "Jane Doe",
 *   "prescription": []
 * }
 * 
 * Expected Response:
 * {
 *   "externalId": 456789,
 *   "name": "janedoe",
 *   "displayName": "Jane Doe",
 *   "prescription": [],
 *   "id": 123,
 *   "profilePicturePath": "https://example.com/profile.png"
 * }
 */
export const createUser = (userData) => {
  return api.post('/api/user', userData);
};

/**
 * GET USER DETAILS
 * Endpoint: GET /api/user/{user_id}
 * Description: Gets detailed information about a specific user
 * 
 * Expected Response:
 * {
 *   "externalId": 456789,
 *   "name": "janedoe",
 *   "displayName": "Jane Doe",
 *   "prescription": [],
 *   "id": 123,
 *   "profilePicturePath": "https://example.com/profile.png"
 * }
 */
export const getUserDetails = (userId) => {
  return api.get(`/api/user/${userId}`);
};

/**
 * GET ALL USERS
 * Endpoint: GET /api/users
 * Description: Gets list of all users
 * 
 * Expected Response:
 * [
 *   {
 *     "externalId": 456789,
 *     "name": "janedoe",
 *     "displayName": "Jane Doe",
 *     "prescription": [],
 *     "id": 123,
 *     "profilePicturePath": "https://example.com/profile.png"
 *   },
 *   ...
 * ]
 */
export const getAllUsers = () => {
  return api.get('/api/users');
};

/**
 * UPDATE USER
 * Endpoint: PATCH /api/user/{user_id}
 * Description: Updates user information
 * 
 * Request Body (all fields optional):
 * {
 *   "externalId": 456789,
 *   "name": "janedoe_updated",
 *   "displayName": "Jane Doe Updated",
 *   "prescription": []
 * }
 * 
 * Expected Response:
 * {
 *   "externalId": 456789,
 *   "name": "janedoe_updated",
 *   "displayName": "Jane Doe Updated",
 *   "prescription": [],
 *   "id": 123,
 *   "profilePicturePath": "https://example.com/profile.png"
 * }
 */
export const updateUser = (userId, userData) => {
  return api.patch(`/api/user/${userId}`, userData);
};

/**
 * DELETE USER
 * Endpoint: DELETE /api/user/{user_id}
 * Description: Deletes a user
 * 
 * Expected Response:
 * {
 *   "success": true,
 *   "message": "User deleted successfully"
 * }
 */
export const deleteUser = (userId) => {
  return api.delete(`/api/user/${userId}`);
};

/**
 * UPLOAD USER PROFILE PICTURE
 * Endpoint: PUT /api/user/{user_id}/profile.png
 * Description: Uploads a profile picture for a specific user
 * 
 * Request: multipart/form-data
 * Form Fields:
 * - file: image file (png, jpg, jpeg, etc.)
 * 
 * File Validation:
 * - File type: image/* (png, jpg, jpeg, gif, webp, etc.)
 * - Max size: 5MB
 * 
 * Expected Response:
 * {
 *   "externalId": 456789,
 *   "name": "janedoe",
 *   "displayName": "Jane Doe",
 *   "prescription": [],
 *   "id": 123,
 *   "profilePicturePath": "https://example.com/user/123/profile.png"
 * }
 */
export const uploadUserProfilePicture = (userId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return api.put(`/api/user/${userId}/profile.png`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

/**
 * PRESCRIBE EXERCISE TO USER
 * Endpoint: PATCH /api/user/{user_id}/prescription_set/{exercise_id}
 * Description: Prescribes an exercise to a specific user with repetition and hold time
 * 
 * Request Body:
 * {
 *   "repetition": 3,
 *   "holdTime": 5
 * }
 * 
 * Expected Response:
 * {
 *   "externalId": 456789,
 *   "name": "janedoe",
 *   "displayName": "Jane Doe",
 *   "prescription": [
 *     {
 *       "exerciseId": 1,
 *       "repetition": 3,
 *       "holdTime": 5
 *     }
 *   ],
 *   "id": 123,
 *   "profilePicturePath": "https://example.com/user/123/profile.png"
 * }
 */
export const prescribeExerciseToUser = (userId, exerciseId, prescriptionData) => {
  return api.patch(`/api/user/${userId}/prescription/set/${exerciseId}`, prescriptionData);
};

export const reorderUserPrescriptions = (userId, exerciseIds) => {
  return api.patch(`/api/user/${userId}/prescription/reorder`, { exerciseIds });
};

/**
 * REVOKE PRESCRIBED EXERCISE FROM USER
 * Endpoint: PATCH /api/user/{user_id}/prescription_unset/{exercise_id}
 * Description: Revokes/removes a prescribed exercise from a specific user
 * 
 * Request Body: None (empty body)
 * 
 * Expected Response:
 * {
 *   "externalId": 456789,
 *   "name": "janedoe",
 *   "displayName": "Jane Doe",
 *   "prescription": [
 *     // Array without the removed exercise
 *   ],
 *   "id": 123,
 *   "profilePicturePath": "https://example.com/user/123/profile.png"
 * }
 */
export const revokePrescribedExerciseFromUser = (userId, exerciseId) => {
  return api.patch(`/api/user/${userId}/prescription/unset/${exerciseId}`);
};

// Untuk file upload
export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

// ============================================================================
// AUTHENTICATION API ENDPOINTS
// ============================================================================

/**
 * LOGIN
 * Endpoint: POST /api/token
 * Description: Authenticate user and get access token
 * 
 * Request Body (form data):
 * {
 *   "username": "janedoe",
 *   "password": "password123"
 * }
 * 
 * Expected Response:
 * {
 *   "access_token": "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9...",
 *   "token_type": "bearer"
 * }
 */
export const login = (credentials) => {
  const formData = new URLSearchParams();
  formData.append('username', credentials.username);
  formData.append('password', credentials.password);
  
  return api.post('/api/token', formData, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    }
  });
};

/**
 * GET CURRENT USER
 * Endpoint: GET /me
 * Description: Get current authenticated user information
 * 
 * Expected Response:
 * {
 *   "id": 123,
 *   "externalId": 456789,
 *   "name": "janedoe",
 *   "displayName": "Jane Doe",
 *   "prescription": [],
 *   "profilePicturePath": "https://example.com/profile.png"
 * }
 */
export const getCurrentUser = () => {
  return api.get('/api/user/0');
};

/**
 * LOGOUT
 * Note: This is a client-side logout function
 * The server doesn't have a logout endpoint since JWT tokens are stateless
 */
export const logout = () => {
  // Clear local storage and redirect to login
  clearAuthData();
  window.location.href = '/login';
};

/**
 * REFRESH TOKEN
 * Note: This implementation checks if the current token is still valid
 * by making a request to /me endpoint
 */
export const refreshToken = async () => {
  try {
    const response = await getCurrentUser();
    return response.data;
  } catch (error) {
    // Token is invalid, clear auth data
    clearAuthData();
    throw error;
  }
};